import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllRoles } from "../../redux/slice/getRolesPermissionListSlice";
import InfoIcon from "../../assets/img/info-user-icon.png";
import viewIcon from "../../assets/img/view-icon.png";
import editIcon from "../../assets/img/edit-icon.png";
import RoleModals from "./RoleModals";
import { useHasPermission } from "hooks/useHasPermission";
import { toast } from "react-toastify";

function RoleTable() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showEditModal, setShowEditModal] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState(null);
  const [editRoleName, setEditRoleName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPermissions, setEditPermissions] = useState([]);
  const { roles, status } = useSelector((state) => state.rolePermission);
  const canView = useHasPermission("Role-view");
  const canEdit = useHasPermission("Role-manage");

  useEffect(() => {
    if (canView && (!roles || roles.length === 0)) {
      dispatch(fetchAllRoles());
    }
  }, [dispatch, canView]);

  if (!canView) {
    return <p className="perm-msg">You don’t have permission to view roles!</p>;
  };

  const handleViewRoleUsers = (roleId) => {
    if (!canView) {
      toast.error("You don't have permission to view role users list", { className: 'toast-error-custom', autoClose: 5000 });
      return;
    }
    navigate(`/admin/role/view-users?roleId=${roleId}`)
  };

  const renderTooltip = (props, description) => (
    <Tooltip id="button-tooltip" {...props}>
      {description}
    </Tooltip>
  );

  return (
    <div className="role-data table-responsive">
      {status === 'loading' && (
        <p className="loading">Loading...</p>
      )}

      {status === 'failed' && (
        <p className="server-error-msg">Unable to connect to server</p>
      )}
      {status === "succeeded" && (
        () => {
          if (roles?.length === 0) {
            return <p className="loading">No record found for role</p>;
          }
          return (
            <table className="role-data-table table table-hover table-vcenter text-nowrap mb-0">
              <thead>
                <tr>
                  <th className="table-row">S.NO.</th>
                  <th className="table-row">TEAM MEMBERS</th>
                  <th className="table-row">ROLE</th>
                  <th className="table-row">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {roles?.map((role, index) => (
                  <tr key={role.roleId}>
                    <td>{index + 1}.</td>
                    <td role="gridcell"
                      onClick={
                        canView
                          ? () => {
                            handleViewRoleUsers(role.roleId)
                          }
                          : undefined
                      } style={{ cursor: canView ? 'pointer' : 'default' }}>{role.users.length} {(role.users.length === 1 || role.users.length === 0) ? "Person" : "Persons"}</td>
                    <td className="rolename">
                      {role.roleName}

                      <OverlayTrigger
                        placement="right"
                        overlay={(props) => renderTooltip(props, role.description)}>
                        <img className="rolename-info" src={InfoIcon} alt="role info" />
                      </OverlayTrigger>
                    </td>
                    <td className="action-column">
                      <button
                        className="edit-role"
                        title={canEdit ? "edit" : "No permission to edit role"}
                        disabled={!canEdit}
                        onClick={
                          canEdit
                            ? () => {
                              setRoleToEdit(role.roleId);
                              setEditRoleName(role.roleName);
                              setEditDescription(role.description);
                              setEditPermissions(role.permissions || []);
                              setShowEditModal(true);
                            }
                            : undefined
                        }
                      >
                        <img
                          src={editIcon}
                          alt="edit"
                          title={canEdit ? "edit" : "No permission to edit role"}
                          style={{ opacity: canEdit ? 1 : 0.5 }}
                        />
                      </button>
                      <button
                        className="delete-role" title={
                          canView
                            ? "view users"
                            : "No permission to show role-users list"
                        }
                        disabled={!canView}
                        onClick={
                          canView
                            ? () => {
                              handleViewRoleUsers(role.roleId)
                            }
                            : undefined
                        }>
                        <img src={viewIcon} alt="view" style={{
                          opacity: canView ? 1 : 0.5
                        }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        })()}

      {showEditModal && (
        <RoleModals
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          roleId={roleToEdit}
          roleName={editRoleName}
          description={editDescription}
          canEdit={canEdit}
          existingPermissions={editPermissions}
        />
      )
      }
    </div>
  );
}

export default RoleTable;
